import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Money } from "./SaudiRiyal";
import { api } from "../lib/api";
import { personMatch } from "../lib/searchText";

const Cell = ({ x }) => {
  if (!x) return <span className="text-slate-300">—</span>;
  return (
    <div className="space-y-0.5">
      {x.out > 0 && <p className="font-semibold text-rose-600">−<Money value={x.out} />{x.draft && <span className="ms-1 text-[9px] text-slate-400">مسودة</span>}</p>}
      {x.in > 0 && <p className="text-[10px] text-amber-700">خُصم <Money value={x.in} /></p>}
    </div>
  );
};

export const DebtHistoryDialog = ({ open, onOpenChange }) => {
  const [n, setN] = useState(6), [d, setD] = useState(null), [q, setQ] = useState(""), [onlyRepeat, setOnlyRepeat] = useState(false);
  useEffect(() => { if (open) { setD(null); api.salaryDebtHistory(n).then(setD).catch(() => setD({ months: [], riders: [] })); } }, [open, n]);
  const rows = (d?.riders || []).filter((r) => (!onlyRepeat || r.repeat) && personMatch(r, q));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="w-[96vw] max-w-5xl max-h-[90vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="debt-history-dialog">
        <DialogHeader className="text-start">
          <DialogTitle>سجل الديون المرحّلة</DialogTitle>
          <DialogDescription className="text-start">المناديب الذين طلع صافي راتبهم بالسالب وتم ترحيله، شهراً بشهر.</DialogDescription>
        </DialogHeader>
        {!d ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /> : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-200 p-3"><p className="text-xs text-slate-500">مناديب عليهم ديون</p><p className="text-xl font-bold" data-testid="debt-count">{d.riders.length}</p></div>
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3"><p className="text-xs text-rose-700">متكرر (شهرين أو أكثر)</p><p className="text-xl font-bold text-rose-700" data-testid="debt-repeat-count">{d.repeat_count}</p></div>
              <div className="col-span-2 rounded-xl border border-amber-200 bg-amber-50 p-3 sm:col-span-1"><p className="text-xs text-amber-700">إجمالي آخر رصيد مرحّل</p><p className="text-xl font-bold text-amber-700"><Money value={d.total_current} /></p></div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-0 flex-1"><Search className="absolute end-2 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم أو رقم المندوب" className="h-9 pe-8" data-testid="debt-search" /></div>
              <select value={n} onChange={(e) => setN(Number(e.target.value))} className="h-9 rounded-lg border border-slate-200 px-2 text-sm" data-testid="debt-months">
                {[3, 6, 12].map((x) => <option key={x} value={x}>آخر {x} أشهر</option>)}
              </select>
              <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={onlyRepeat} onChange={(e) => setOnlyRepeat(e.target.checked)} data-testid="debt-only-repeat" /> المتكرر فقط</label>
            </div>
            {!rows.length ? <p className="py-8 text-center text-sm text-slate-400" data-testid="debt-empty">لا توجد ديون مرحّلة في هذه الفترة</p> : (
              <div className="max-w-full overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-right text-xs">
                  <thead className="sticky top-0 bg-slate-50 text-slate-500"><tr><th className="p-2">المندوب</th>{d.months.map((m) => <th key={m} className="p-2 whitespace-nowrap" dir="ltr">{m}</th>)}<th className="p-2">أشهر بالسالب</th><th className="p-2">الإجمالي</th></tr></thead>
                  <tbody>{rows.map((r) => (
                    <tr key={r.employee_id} className={`border-t border-slate-100 ${r.repeat ? "bg-rose-50/40" : ""}`} data-testid={`debt-row-${r.employee_id}`}>
                      <td className="p-2 whitespace-nowrap"><p className="font-semibold">{r.name}</p><p className="text-[10px] text-slate-400">{r.rider_id}{r.branch ? ` · ${r.branch}` : ""}</p></td>
                      {d.months.map((m) => <td key={m} className="p-2 whitespace-nowrap"><Cell x={r.months[m]} /></td>)}
                      <td className="p-2">{r.repeat ? <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 font-bold text-rose-700" data-testid={`debt-repeat-${r.employee_id}`}><AlertTriangle className="h-3 w-3" /> {r.negative_months}</span> : r.negative_months}</td>
                      <td className="p-2 font-bold text-rose-600"><Money value={r.total_out} /></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-slate-500">«−» مبلغ رُحّل من راتب الشهر للشهر التالي · «خُصم» دين سابق خُصم في هذا الشهر.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
